import { useState, useContext, useEffect, useMemo } from 'react';
import { CatalogDispatchContext, CartContext } from '../store/CatalogContext';
import ProductUpsells from './ProductUpsells';
import { getDisplayPrice, getOrigPrice, shouldIncludeFees } from '../utils/amountHelper';
import { formatCurrency } from '../utils/formatCurrency';
import UpsellDescriptionModal from './UpsellDescriptionModal';
import useBrand from '../hooks/useBrand';
import getCartUrl from '../utils/getCartUrl';
import { useCartActions } from '../hooks/useCartActions';

import type { Product, UpsellItem, ProductState, ProductBulkTier } from '../types/product';
import type { CartProduct } from '../types/cart';

interface ProductCtaCardProps {
  product: Product;
}

export default function ProductCtaCard({ product }: ProductCtaCardProps) {
  const brand = useBrand();
  const domain = brand?.domain;
  const cartUrl = useMemo(() => getCartUrl(domain), [domain]);

  const catalogDispatch = useContext(CatalogDispatchContext);
  const cart = useContext(CartContext);

  const showFees = shouldIncludeFees(product?.id) && product.pricing.agencyFee > 0;

  const [productState, setProductState] = useState<ProductState>({
    productId: product?.id || null,
    productInCart: null,
    quantity: 1,
    upsells: product?.upsellProducts || [],
    selectedUpsellProductIds: [],
    isUpdatingCart: false,
    isUpsellUpdated: false,
    isQuantityUpdated: false,
  });

  // console.log('ProductCtaCard', product);

  const { addToCart, updateCart, updateUpsells } = useCartActions({
    setState: setProductState,
    catalogDispatch,
  });

  // setting upsellState from the product and the cart
  // list of upsell items is from product API and selected state is from the cart
  useEffect(() => {
    // dont update state if the cart is currently being updated
    // this prevents the product state from being overwritten while user is making changes or reloading the page
    if (productState.isUpdatingCart) return;

    const thisProductInCart = cart?.cartProducts?.find((item: CartProduct) => item.productId === product?.id);
    const cartProductUpsell = thisProductInCart?.upSellProducts;

    setProductState((prevState) => ({
      ...prevState,
      productId: product?.id || null,
      productInCart: typeof thisProductInCart === 'undefined' ? null : thisProductInCart,
      quantity: thisProductInCart ? thisProductInCart.quantity : 1,
      // TODO: this might not be needed here since we have access to the product upsell directly
      upsells: product?.upsellProducts?.map((upsell) => ({
        ...upsell,
        // this is setting the isSelected flag for each product upsell
        isSelected: cartProductUpsell?.find((item: CartProduct) => item.productId === upsell.id)?.isSelected || false,
      })),
    }));
  }, [product, cart, productState.isUpdatingCart]);

  // checking if upsellState and/or quantity has been updated
  // this checks the cart's upsell isSelected flags against the current upsellState
  useEffect(() => {
    const thisProductInCart = cart?.cartProducts?.find((item: CartProduct) => item.productId === product?.id);
    const cartUpsells = thisProductInCart?.upSellProducts || [];
    const isQuantityUpdated = thisProductInCart && thisProductInCart.quantity !== productState.quantity;

    // Compare only isSelected flags for each upsell
    const upsellMap = new Map((productState.upsells ?? []).map((u) => ['id' in u ? u.id : u.productId, u]));
    const cartUpsellMap = new Map(cartUpsells.map((cu) => [cu.productId, cu]));

    const isUpsellUpdated =
      (productState.upsells ?? []).some((u) => {
        const cu = cartUpsellMap.get('id' in u ? u.id : u.productId);
        return cu ? cu.isSelected !== u.isSelected : u.isSelected !== false;
      }) ||
      cartUpsells.some((cu) => {
        const u = upsellMap.get(cu.productId);
        return u ? u.isSelected !== cu.isSelected : cu.isSelected !== false;
      });

    setProductState((prevState) => ({
      ...prevState,
      isUpsellUpdated: isUpsellUpdated && thisProductInCart ? true : false,
      isQuantityUpdated: isQuantityUpdated ? true : false,
    }));
  }, [product, cart, productState.upsells, productState.quantity]);

  const handleBulkModal = () => {
    catalogDispatch({
      type: 'UX',
      payload: {
        showBulkRates: {
          name: product?.name || '',
          productBulkTiers: product?.productBulkTiers || [],
        },
      },
    });
  };

  // preventing non-number entry for the qty field
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const allowedKeys = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      'Backspace',
      'Delete',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Enter',
      'Control',
      'Meta',
      'a',
    ];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleQtyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    // console.log(event);
    const eventType = event.type;
    const target = event.target as HTMLInputElement;
    const { value } = target;

    // Only allow empty string or a valid number
    const parsedValue = value === '' ? '' : Number(value);

    setProductState((prevState) => ({
      ...prevState,
      quantity: eventType === 'blur' && parsedValue === '' ? productState?.productInCart?.quantity ?? 1 : parsedValue,
    }));
  };

  const handleQtyBtnClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    const target = event.target as HTMLButtonElement;
    const { name } = target;

    if (name === 'decrement' && typeof productState.quantity === 'number' && productState.quantity > 1) {
      setProductState((prevState) => ({
        ...prevState,
        quantity: typeof prevState.quantity === 'number' ? prevState.quantity - 1 : prevState.quantity,
      }));
    }
    if (name === 'increment' && typeof productState.quantity === 'number') {
      setProductState((prevState) => ({
        ...prevState,
        quantity: typeof prevState.quantity === 'number' ? prevState.quantity + 1 : prevState.quantity,
      }));
    }
  };

  // Use useCartActions hook for cart operations
  const handleCartUpdate = () => {
    // if product state is unchanged show the cart widget
    if (productState?.productInCart && !productState.isQuantityUpdated && !productState?.isUpsellUpdated) {
      // console.log('showCartWidget');
      catalogDispatch({
        type: 'UX',
        payload: { showCartWidget: true },
      });
      return;
    }

    if (!productState?.productInCart) {
      // console.log('addToCart');
      addToCart(productState);
      return;
    }

    if (productState?.isUpsellUpdated) {
      // console.log('updateUpsells');
      updateUpsells(productState);
    } else {
      // console.log('updateCart');
      updateCart(productState);
    }
  };

  const handleBuyNow = () => {
    const redirectToCart = () => {
      window.location.href = cartUrl;
    };

    // if product state is unchanged send user to the cart directly
    if (productState.productInCart && !productState.isQuantityUpdated && !productState.isUpsellUpdated) {
      window.location.href = cartUrl;
      return;
    }

    if (!productState.productInCart) {
      addToCart(productState, redirectToCart);
      return;
    }

    if (productState.isUpsellUpdated) {
      updateUpsells(productState, redirectToCart);
    } else {
      updateCart(productState, redirectToCart);
    }
  };

  const discountTierAmount = useMemo(() => {
    const getDiscountTier = (tiers: ProductBulkTier[], quantity: number) => {
      const matches = tiers.filter((tier) => quantity >= tier.minQuantity);
      return matches.length ? matches[matches.length - 1] : null;
    };

    const discountTier = getDiscountTier(
      product?.productBulkTiers ?? [],
      typeof productState.quantity === 'number' ? productState.quantity : 1
    );
    return discountTier ? discountTier.unitDiscount : 0;
  }, [product, productState.quantity]);

  // taking into account bulk pricing tiers in the pricing display and calculations
  const displayPrice = useMemo(() => {
    let amount = 0;

    if (!product?.pricing) {
      return amount;
    }

    // if bulk tier discount is greater than combinedDiscount, use bulk tier price for calculations
    if (discountTierAmount > product?.pricing?.combinedDiscount) {
      return showFees
        ? product.pricing.listPrice - discountTierAmount + product.pricing.agencyFee
        : product.pricing.listPrice - discountTierAmount;
    }

    return showFees
      ? product.pricing.listPrice - product?.pricing?.combinedDiscount + product.pricing.agencyFee
      : product.pricing.listPrice - product?.pricing?.combinedDiscount;
  }, [product, productState.quantity]);

  // this does not account for upsells replacement since we dont display that upsell in the product description page
  const totalPrice = useMemo(() => {
    const upsellTotal = (productState.upsells ?? []).reduce(
      (acc, item) => (item.isSelected ? acc + getDisplayPrice(item, true) : acc),
      0
    );

    return (displayPrice + upsellTotal) * (typeof productState.quantity === 'number' ? productState.quantity : 1);
  }, [displayPrice, productState]);

  return (
    <>
      <div
        className={`card product-card product-cta-card ${
          productState.isUpdatingCart || (cart?.isUpdating && productState.productInCart) ? 'is-updating' : ''
        }`}
        data-pid={product?.id}
      >
        <div className="card-body p-sm-4 p-lg-3 p-xl-4">
          <h2 className="fs-5 mb-3">{product?.name}</h2>
          <div className="product-qty d-flex align-items-center justify-content-between py-1">
            <div className="cart-item-qty d-flex align-items-center flex-wrap">
              <div className="input-group me-3 notranslate">
                <button
                  className="qty-btn btn btn-sm btn-light"
                  name="decrement"
                  type="button"
                  aria-label="Decrement"
                  disabled={productState.quantity === 1}
                  onClick={handleQtyBtnClick}
                >
                  &#8722;
                </button>
                <label className="visually-hidden" htmlFor={`qty-${product?.id}`}>
                  Quantity
                </label>
                <input
                  aria-label="Item Quantity"
                  className="qty-input form-control"
                  id={`qty-${product?.id}`}
                  pattern="[0-9]"
                  step="1"
                  min="1"
                  name="quantity"
                  type="text"
                  value={productState.quantity}
                  onChange={handleQtyChange}
                  onBlur={handleQtyChange}
                  onKeyDown={handleKeyDown}
                />
                <button
                  className="qty-btn btn btn-sm btn-light"
                  name="increment"
                  type="button"
                  aria-label="Increment"
                  onClick={handleQtyBtnClick}
                >
                  &#43;
                </button>
              </div>

              {product?.productBulkTiers && product?.productBulkTiers.length > 0 && (
                <div className="bulk-discount-toggle d-inline-block text-muted lh-1 my-2">
                  <button className="btn-toggle fw-400 small text-reset lh-1 p-0" type="button" onClick={handleBulkModal}>
                    Bulk Rates<span className="info-icon align-middle">?</span>
                  </button>
                </div>
              )}
            </div>
            <div className="price text-end fw-600 h-100">
              {product?.pricing &&
                (product.pricing.combinedDiscount > 0 || discountTierAmount > product?.pricing?.combinedDiscount) && (
                  <>
                    <span className="orig-price me-0">{product && formatCurrency(getOrigPrice(product))}</span>
                    <br />
                  </>
                )}
              {product && formatCurrency(displayPrice)}
            </div>
          </div>

          <ProductUpsells upsells={productState.upsells as UpsellItem[]} setProductState={setProductState} />

          <div className="item-total d-flex align-items-center justify-content-between mt-4">
            <span className="fw-600 text-end flex-grow-1 pe-3">Total:</span>
            <span className="price text-end fs-5 fw-700 w-auto">{formatCurrency(totalPrice)}</span>
          </div>
        </div>
        <div className="card-footer bg-transparent border-0 pb-3 pb-sm-4 pb-lg-3 pb-xl-4 px-sm-4 px-lg-3 px-xl-4">
          <button
            className={`add-to-cart-btn btn btn-success d-block fw-700 w-100 mb-2 ${
              productState.productInCart && !productState.isQuantityUpdated && !productState.isUpsellUpdated
                ? 'view-cart'
                : ''
            }`}
            type="button"
            onClick={handleCartUpdate}
          >
            {productState.productInCart && !productState.isQuantityUpdated && !productState.isUpsellUpdated
              ? 'View In Cart'
              : productState.productInCart && (productState.isQuantityUpdated || productState.isUpsellUpdated)
              ? 'Update Cart'
              : 'Add to Cart'}
          </button>
          <button className="buy-now-btn btn btn-outline-success d-block fw-700 w-100" onClick={handleBuyNow} type="button">
            Buy Now
          </button>
        </div>
      </div>

      <UpsellDescriptionModal />
    </>
  );
}
